import { Component, OnInit } from '@angular/core';
import { EntityImage } from '../../classes/entityimage';
import { Project } from '../../classes/project';
import { WorkorderService } from '../../services/workorder.service';
import { CodaltComponent } from '../codalt/codalt.component';
import { Workorder } from '../../classes/workorder';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { UserService } from '../../services/user.service';
import { User } from '../../classes/user';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { Features } from '../../services/auth/features';
import { Roles } from '../../services/auth/roles';
import { LocalStorage } from '../../utils/storage';
import { DrawerService } from '../generic/drawer/drawer.service';
import { WorkorderDetailComponent } from '../workorder-detail/workorder-detail.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Routenames } from '../../routenames';

@Component({
    selector: 'app-workorders',
    templateUrl: './workorders.component.html',
    styleUrl: './workorders.component.scss',
})
export class WorkordersComponent extends CodaltComponent implements OnInit {
    private readonly sessionKey = 'workordersFormData';

    entities: EntityImage[];
    projects: Project[];
    workorders: Workorder[];
    managers: User[];
    workorders$: Subscription;

    form = new FormGroup({
        search: new FormControl(null),
        userId: new FormControl(null),
        date: new FormControl(),
        history: new FormControl(true),
        incomplete: new FormControl(true),
        remark: new FormControl(false),
        initialized: new FormControl(false),
    });

    constructor(
        private workorderService: WorkorderService,
        private userService: UserService,
        private title: Title,
        private drawerService: DrawerService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();
    }

    ngOnInit() {
        this.title.setTitle('Werkopdrachten - TSNed App');

        const savedFormData = this.getFormDataFromSession();

        if (savedFormData) {
            this.form.patchValue(savedFormData.formData ?? savedFormData);
        }

        if (AuthorisationService.hasRole(Roles.Manager)) {
            const hasUserIdKey = savedFormData && Object.prototype.hasOwnProperty.call(savedFormData.formData ?? savedFormData, 'userId');

            if (!hasUserIdKey) {
                this.form.controls.userId.setValue(LocalStorage.user.id);
            }
        }

        this.loadWorkorders();

        this.subscriptions.add(
            this.form.valueChanges
                .pipe(
                    tap(() => {
                        this.saveFormDataToSession({
                            formData: this.form.getRawValue(),
                            scrollTop: window.scrollY,
                        });
                        this.workorders = null;
                    }),
                    debounceTime(200),
                )
                .subscribe(() => {
                    this.loadWorkorders();
                }),
        );

        if (AuthorisationService.hasFeature(Features.UsersManagers)) {
            this.subscriptions.add(
                this.userService.getManagers().subscribe((managers) => {
                    this.managers = managers.data;
                }),
            );
        }

        this.subscriptions.add(
            this.route.queryParams.subscribe((params: { workorderId: number }) => {
                if (params.workorderId) {
                    const drawerInstance = this.drawerService.open(WorkorderDetailComponent, 'right', params);
                    if (drawerInstance) {
                        this.subscriptions.add(
                            drawerInstance.closed.subscribe(() => {
                                this.router.navigate([Routenames.WorkOrders], { queryParams: {}, replaceUrl: true });
                            }),
                        );
                    }
                }
            }),
        );
    }

    loadWorkorders() {
        this.workorders$?.unsubscribe();
        this.workorders$ = this.workorderService.getPlannedWorkorders(this.form.value).subscribe((wo) => {
            this.workorders = wo.data;
            const savedFormData = this.getFormDataFromSession();

            setTimeout(() => {
                const savedScroll = savedFormData?.scrollTop;
                if (typeof savedScroll === 'number') {
                    window.scrollTo({ top: savedScroll });
                }
            }, 0);
        });
    }

    openWithRemarkFilter(event, workorder: Workorder) {
        event.stopPropagation();
        event.preventDefault();
        this.router.navigate([`/${Routenames.Projects}/${workorder.project_id}`], {
            queryParams: { withRemark: 'true', workorder: workorder.id },
        });
    }

    override ngOnDestroy() {
        this.workorders$?.unsubscribe();
        super.ngOnDestroy();

        this.saveScrollPosition();
    }

    private saveScrollPosition = () => {
        // Sla huidige scrollpositie samen met form data op
        this.saveFormDataToSession({
            formData: this.form.getRawValue(),
            scrollTop: window.scrollY,
        });
    };

    private getFormDataFromSession(): any | null {
        const raw = sessionStorage.getItem(this.sessionKey);
        if (raw === null) {
            return null;
        }
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    private saveFormDataToSession(data: any): void {
        sessionStorage.setItem(this.sessionKey, JSON.stringify(data));
    }
}
